import { useEffect } from "react";
import { stringTocamelCase } from "../_helpers/utils";

const useWidgetAutoAnimator = ({
  rootSelector = ".preview-content.customScroll",
  threshold = 0.5,
  margin = "0px",
  contextWidgets = [],
  active,
} = {}) => {
  const playerHelper = {
    animationSpeedToDurationMsObj: {
      Fast: 500,
      Medium: 1000,
      Slow: 2000,
    },
    getWidgetData: ({ widgetObj, pageDurationMs }) => {
      const { startTime = 0, endTime = pageDurationMs } = widgetObj?.duration ?? {};
      const startTimeMs = parseFloat(startTime) * 1000;
      const endTimeMs = parseFloat(endTime) * 1000;
      const stopTimeMs = endTimeMs > pageDurationMs ? pageDurationMs : endTimeMs;
      const widgetNode = document.getElementById(widgetObj.id);
      const assetType = widgetObj.data["data-asset-type"];
      const source = widgetObj.data["data-source"];

      return { startTimeMs, stopTimeMs, widgetNode, assetType, source };
    },

    handleAnimationPlay: ({ widgetId, widgetNode }) => {
      let widgetObj = contextWidgets?.find(w => w.id === widgetId);
      if (!widgetObj) return;
      const { startTimeMs, stopTimeMs, assetType, source } = playerHelper.getWidgetData({ widgetObj });

      if (startTimeMs > 0) {
        setTimeout(() => {
          widgetNode.style.visibility = "";
          playerHelper.animationPlay({
            widgetNode,
            widgetObj,
          });
        }, startTimeMs);
      } else {
        widgetNode.style.visibility = "";
        playerHelper.animationPlay({
          widgetNode,
          widgetObj,
        });
      }
    },

    animationPlay: ({ widgetNode, widgetObj }) => {
      const showEntryAnimation = widgetObj.data["data-entrance-animation"];
      const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
      const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
      const entryAnimationDurationMs =
        showEntryAnimation && playerHelper.animationSpeedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

      if (!showEntryAnimation) return;

      let animationNode = document.createElement("div");
      animationNode.classList.add("animation-node");
      animationNode.style.height = "100%";
      animationNode.style.width = "100%";
      [...widgetNode.childNodes].forEach(child => animationNode.append(child));
      widgetNode.innerHTML = "";
      widgetNode.append(animationNode);

      if (showEntryAnimation) {
        // entry animation start
        animationNode.classList.add("play-animation");
        animationNode.classList.add(entryAnimationClassName);
        animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
      }
    },

    getDynamicThreshold: (widget, root, defaultThreshold) => {
      const rootHeight = root?.clientHeight || window.innerHeight;
      const widgetHeight = widget.offsetHeight;

      // Giant widgets → trigger on entry
      if (widgetHeight > rootHeight * 1.5) {
        return 0;
      }

      // Normal tall widgets
      if (widgetHeight > rootHeight) {
        const maxVisibleRatio = rootHeight / widgetHeight;

        return Math.max(0.02, Math.min(defaultThreshold, maxVisibleRatio));
      }

      return defaultThreshold;
    },
  };

  const setupIntersectionObserver = () => {
    if (!contextWidgets?.length) return;

    const root = document.querySelector(rootSelector);
    const animated = new Set();
    const observers = [];

    const observeWidgets = () => {
      const widgets = document.querySelectorAll("[data-id][data-class='dhp-root-widgets']");
      if (!widgets.length) {
        requestAnimationFrame(observeWidgets);
        return;
      }

      widgets.forEach(widget => {
        const widgetId = widget.getAttribute("data-id");
        const widgetObj = contextWidgets.find(w => w.id === widgetId);
        if (!widgetObj) return;

        const hasEntranceAnimation = widgetObj?.data?.["data-entrance-animation"];

        if (hasEntranceAnimation) {
          widget.style.visibility = "hidden";
        }

        const dynamicThreshold = playerHelper.getDynamicThreshold(widget, root, threshold);

        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting || animated.has(widgetId)) return;

              playerHelper.handleAnimationPlay({
                widgetId,
                widgetNode: widget,
              });

              animated.add(widgetId);
              observer.unobserve(widget);
            });
          },
          {
            root,
            rootMargin: margin,
            threshold: dynamicThreshold,
          }
        );

        observer.observe(widget);
        observers.push(observer);
      });
    };

    observeWidgets();

    return () => {
      observers.forEach(o => o.disconnect());
    };
  };

  useEffect(() => {
    if (!active) return;
    const cleanup = setupIntersectionObserver();
    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [contextWidgets, active]);
};

export default useWidgetAutoAnimator;
